import type { OfficeContext } from "./types";

export type OfficeReadyState = {
  host: string;
  isOfficeReady: boolean;
};

const fallbackHost = "Browser";

function getOffice(): typeof Office | undefined {
  return typeof Office === "undefined" ? undefined : Office;
}

function asyncOfficeCall<T>(
  run: (callback: (result: Office.AsyncResult<T>) => void) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    run((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "Office host operation failed."));
      }
    });
  });
}

export async function waitForOffice(): Promise<OfficeReadyState> {
  const office = getOffice();
  if (!office?.onReady) {
    return { host: fallbackHost, isOfficeReady: false };
  }

  const info = await office.onReady();
  return {
    host: info.host?.toString() ?? fallbackHost,
    isOfficeReady: Boolean(info.host),
  };
}

export async function getOfficeContext(host: string): Promise<OfficeContext> {
  const office = getOffice();
  if (!office?.context?.document) {
    return { host, selection: "" };
  }

  try {
    const selection = await asyncOfficeCall<string>((callback) => {
      office.context.document.getSelectedDataAsync(office.CoercionType.Text, callback);
    });

    return { host, selection: selection.trim() };
  } catch {
    return { host, selection: "" };
  }
}

// @lat: [[lat.md\office-plugin#VoltBox Assistant Office add-in#Taskpane workflow]]
export async function insertIntoSelection(content: string): Promise<void> {
  const office = getOffice();
  if (!office?.context?.document) {
    throw new Error("Open this task pane inside Word, Excel, or PowerPoint to insert content.");
  }

  await asyncOfficeCall<void>((callback) => {
    office.context.document.setSelectedDataAsync(
      content,
      { coercionType: office.CoercionType.Text },
      callback,
    );
  });
}
